import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  AlertAction,
  AlertDescription,
  AlertTitle,
  Button,
  Skeleton,
} from '@monksflow/monks-ui';

export interface PlatformChooserProps {
  isPending: boolean;
  isError: boolean;
  /** True when the platform answered, and answered with nothing. */
  isEmpty: boolean;
  onRetry: () => void;
  label: string;
  errorMessage?: string;
  children: ReactNode;
}

/**
 * Renders one of four states around a chooser fed by a platform API.
 *
 * The states exist because collapsing them is a bug this codebase has already shipped
 * twice: `list?.supported && (…)` renders nothing when the query errors, so "we could not
 * ask the platform" looks exactly like "this account has none".
 *
 * The invariant: only an explicit negative may hide a chooser. An absent value must never
 * mean "no".
 */
export function PlatformChooser({
  isPending,
  isError,
  isEmpty,
  onRetry,
  label,
  errorMessage,
  children,
}: PlatformChooserProps) {
  const { t } = useTranslation('projects');

  if (isPending) {
    return <Skeleton className="h-10 w-full" data-testid="platform-chooser-pending" />;
  }

  if (isError) {
    return (
      <Alert variant="destructive" data-testid="platform-chooser-error">
        <AlertTitle>
          {t('performanceCampaignLink.platformChooser.couldNotLoadOptions', {
            label: label.toLowerCase(),
          })}
        </AlertTitle>
        <AlertDescription>
          {errorMessage ?? t('performanceCampaignLink.platformChooser.thePlatformDidNotAnswer')}
        </AlertDescription>
        <AlertAction>
          <Button variant="outline" size="sm" onClick={onRetry}>
            {t('performanceCampaignLink.platformChooser.tryAgain')}
          </Button>
        </AlertAction>
      </Alert>
    );
  }

  if (isEmpty) {
    return (
      <p className="text-sm text-muted-foreground" data-testid="platform-chooser-empty">
        {t('performanceCampaignLink.platformChooser.noCampaignsOnThisAccount')}
      </p>
    );
  }

  return <>{children}</>;
}
